import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatPhone, type Capability } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { AddTeamMember } from '../components/AddTeamMember.js';

/**
 * Équipe — who can sign in, as what, and in which of the businesses.
 *
 * Three things this screen shows that the old one could not, each because of a decision in the
 * data model rather than a decision about layout:
 *
 * **What a role actually permits.** The previous model was one flag: administrator, or not. So
 * "what will this person be able to see?" had no answer you could read, and the safe move was
 * to make everyone an administrator — which is what happened, seven times over. Here choosing a
 * role shows the capabilities it grants and, when changing one, what is gained and lost.
 *
 * **That a person is one person.** Five of these people work in more than one of the three
 * businesses, two in all three. Previously each was a separate record per business with its own
 * colour, so the same human was a different colour depending on which schedule you looked at,
 * and nowhere told you that switching someone off here leaves them working next door. That
 * warning is on this screen.
 *
 * **Whether the account is used.** Visits and hours per person, because "can we remove this
 * one?" is the question this page exists to answer, and an unused account looks exactly like a
 * busy one without it.
 */

type Translatable = { fr: string; en: string };

type RoleRef = {
  id: string;
  key: string;
  name: Translatable;
  landing: 'portal' | 'field';
  capabilities: Capability[];
};

type RoleRow = RoleRef & {
  description: Translatable | null;
  isSystem: boolean;
  isProtected: boolean;
  memberCount: number;
};

type OtherTenant = {
  tenantId: string;
  tenantName: string;
  roleKey: string;
  roleName: Translatable;
  status: string;
};

type Member = {
  id: string;
  email: string | null;
  name: string;
  phone: string | null;
  color: string | null;
  locale: string;
  role: RoleRef;
  status: 'active' | 'inactive' | 'archived';
  /** Absent — not blanked — for anyone without `time.rate.read`. */
  hourlyRate?: string | null;
  canSignIn: boolean;
  hasSession: boolean;
  lastSeenAt: string | null;
  otherTenants: OtherTenant[];
  activity: { visits: number; loggedMinutes: number; lastVisitAt: string | null };
};

type TeamData = {
  members: Member[];
  ratesVisible: boolean;
  canManage: boolean;
  counts: { total: number; active: number; technicians: number; withoutLogin: number };
};

type RolesData = {
  roles: RoleRow[];
  allCapabilities: Capability[];
  /** Capabilities the viewer holds no right to hand out. */
  cannotGrant: Capability[];
  canManage: boolean;
};

/** Role names are per business, so they are read from the row rather than a table in here. */
const label = (value: Translatable | null | undefined): string => value?.fr ?? '—';

/**
 * Capabilities in plain French.
 *
 * Written as what the person can do, not as the identifier: "voit les prix de revient et les
 * marges" is checkable against reality in a way that `billing.cost.read` is not.
 */
const CAPABILITY_LABEL: Partial<Record<Capability, string>> = {
  'job.read.assigned': 'Voit les travaux qui lui sont assignés',
  'job.read.all': 'Voit tous les travaux',
  'job.write': 'Crée et modifie les travaux',
  'job.assign': 'Assigne le travail à l’équipe',
  'job.complete': 'Marque le travail comme terminé',
  'customer.read.related': 'Voit les clients de ses travaux',
  'customer.read.all': 'Voit tous les clients',
  'customer.write': 'Modifie les fiches clients',
  'customer.contact': 'Voit téléphones et courriels',
  'billing.read.job': 'Voit le solde du travail en cours',
  'billing.read.all': 'Consulte toutes les soumissions et factures',
  'billing.write': 'Crée et modifie soumissions et factures',
  'billing.cost.read': 'Voit les prix de revient et les marges',
  'payment.collect': 'Encaisse un paiement sur place',
  'payment.read.all': 'Voit tous les paiements reçus',
  'payment.refund': 'Effectue des remboursements',
  'time.write.own': 'Inscrit ses propres heures',
  'time.read.own': 'Consulte ses propres heures',
  'time.read.all': 'Voit les heures de tout le monde',
  'time.approve': 'Approuve les feuilles de temps',
  'time.rate.read': 'Voit les taux horaires',
  'thread.read.related': 'Lit les conversations de ses travaux',
  'thread.read.all': 'Lit toutes les conversations',
  'thread.write': 'Écrit dans les conversations',
  'call.read.all': 'Consulte l’historique d’appels',
  'call.place': 'Passe des appels',
  'report.read': 'Consulte les rapports et les revenus',
  'settings.read': 'Consulte les réglages',
  'settings.write': 'Modifie les réglages',
  'user.manage': 'Gère les accès de l’équipe',
  'tenant.manage': 'Gère l’entreprise et l’abonnement',
};

const STATUS_LABEL: Record<Member['status'], string> = {
  active: 'Actif',
  inactive: 'Désactivé',
  archived: 'Archivé',
};

export function TeamScreen() {
  const { person, filter, q, view = 'people' } = useSearch({ from: '/team' });
  const navigate = useNavigate();

  const setSearch = (patch: Record<string, unknown>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }), replace: true });

  const query = useQuery({ queryKey: ['team'], queryFn: () => request<TeamData>('/team') });
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: () => request<RolesData>('/roles') });

  const data = query.data;
  const term = (q ?? '').trim().toLowerCase();

  const rows = (data?.members ?? []).filter((member) => {
    if (filter === 'active' && member.status !== 'active') return false;
    if (filter === 'former' && member.status === 'active') return false;
    // "Technicians" means anyone whose role signs in to the field app, whatever it is called.
    if (filter === 'technicians' && member.role.landing !== 'field') return false;
    if (!term) return true;
    return [member.name, member.email, member.phone].some((v) => v?.toLowerCase().includes(term));
  });

  const selected = data?.members.find((m) => m.id === person) ?? null;
  const roleOf = (member: Member): RoleRow | null =>
    rolesQuery.data?.roles.find((r) => r.id === member.role.id || r.key === member.role.key) ?? null;

  return (
    <div className="screen team">
      <header className="team__head">
        <div>
          <h1 className="team__title">Équipe</h1>
          {data && (
            <p className="team__summary">
              <span>{data.counts.total} personnes</span>
              <span aria-hidden="true"> · </span>
              <span>{data.counts.active} actives</span>
              <span aria-hidden="true"> · </span>
              <span>{data.counts.technicians} techniciens</span>
              {data.counts.withoutLogin > 0 && (
                <>
                  <span aria-hidden="true"> · </span>
                  {/* Not an error. They exist so their hours stay attributable and so they
                      can still be put on a visit; they simply have no way in. */}
                  <span className="team__muted">{data.counts.withoutLogin} sans accès à l’application</span>
                </>
              )}
            </p>
          )}
        </div>

        <div className="team__controls">
          <input
            value={q ?? ''}
            onChange={(e) => setSearch({ q: e.target.value || undefined })}
            placeholder="Nom, courriel, téléphone…"
            aria-label="Rechercher une personne"
            className="team__search"
          />
          <div className="team__filters" role="group" aria-label="Filtres">
            {(
              [
                ['active', 'Actifs'],
                ['technicians', 'Techniciens'],
                ['former', 'Anciens'],
                ['all', 'Tous'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`team__filter${filter === value ? ' team__filter--on' : ''}`}
                aria-pressed={filter === value}
                onClick={() => setSearch({ filter: value })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/*
        * Two views of the same subject: the people, and the roles they hold. Separate rather
        * than combined because they answer opposite questions — "what can this person do?"
        * versus "who can do this?" — and the second is the one a per-user permission model can
        * never answer.
        */}
      <div className="team__tabs" role="tablist" aria-label="Vue">
        {(
          [
            ['people', `Personnes${data ? ` (${data.counts.total})` : ''}`],
            ['roles', `Rôles${rolesQuery.data ? ` (${rolesQuery.data.roles.length})` : ''}`],
          ] as const
        ).map(([value, text]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={view === value}
            className={`team__tab${view === value ? ' team__tab--on' : ''}`}
            onClick={() => setSearch({ view: value === 'people' ? undefined : value, person: undefined })}
          >
            {text}
          </button>
        ))}
      </div>

      {query.isLoading && <p className="screen__loading">Chargement…</p>}
      {query.isError && <div className="notice notice--error">La liste de l’équipe est inaccessible.</div>}

      {view === 'roles' && rolesQuery.data && (
        <RolesView data={rolesQuery.data} members={data?.members ?? []} />
      )}

      {/*
        Hiring, above the list rather than at the end of it: with 31 people the bottom of the
        table is a scroll away, and this is the one action on this screen that is not a
        correction to somebody already here.
      */}
      {view === 'roles' ? null : rolesQuery.data?.canManage ? (
        <AddTeamMember roles={rolesQuery.data.roles} canSetRate={!!data?.ratesVisible} />
      ) : null}

      {view === 'roles' ? null : (
      <div className="team__body">
        <table className="table team__table">
          <caption>
            {rows.length} personne{rows.length === 1 ? '' : 's'}
          </caption>
          <thead>
            <tr>
              <th scope="col">Personne</th>
              <th scope="col">Rôle</th>
              {data?.ratesVisible && (
                <th scope="col" style={{ textAlign: 'right' }}>
                  Taux
                </th>
              )}
              <th scope="col" style={{ textAlign: 'right' }}>
                Visites
              </th>
              <th scope="col" style={{ textAlign: 'right' }}>
                Heures
              </th>
              <th scope="col">Accès</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((member) => (
              <tr
                key={member.id}
                className={`team__row${person === member.id ? ' team__row--open' : ''}${
                  member.status !== 'active' ? ' team__row--former' : ''
                }`}
              >
                <th scope="row" className="cell">
                  <button
                    type="button"
                    className="team__person"
                    onClick={() => setSearch({ person: member.id })}
                    aria-expanded={person === member.id}
                  >
                    <span
                      className="team__swatch"
                      style={{ background: member.color ?? 'var(--zinc-400)' }}
                      aria-hidden="true"
                    />
                    <span className="team__personText">
                      <span className="team__name">{member.name}</span>
                      {member.email && <span className="team__email">{member.email}</span>}
                    </span>
                  </button>
                </th>

                <td className="cell">
                  <span className={`chip chip--role chip--${member.role.key}`}>{label(member.role.name)}</span>
                  {member.otherTenants.length > 0 && (
                    <span
                      className="team__alsoIn"
                      title={member.otherTenants
                        .map((other) => `${other.tenantName} — ${label(other.roleName)}`)
                        .join('\n')}
                    >
                      + {member.otherTenants.length} autre{member.otherTenants.length === 1 ? '' : 's'} entreprise
                      {member.otherTenants.length === 1 ? '' : 's'}
                    </span>
                  )}
                </td>

                {data?.ratesVisible && (
                  <td className="cell numeric money">
                    {member.hourlyRate ? (
                      `${member.hourlyRate} $/h`
                    ) : (
                      // Blank means never entered. Saying so beats showing 0,00 $, which
                      // would flow into labour cost as though it were a fact.
                      <span className="team__unset">non défini</span>
                    )}
                  </td>
                )}

                <td className="cell numeric tnum">{member.activity.visits || '—'}</td>
                <td className="cell numeric tnum">
                  {member.activity.loggedMinutes > 0 ? Math.round(member.activity.loggedMinutes / 60) : '—'}
                </td>

                <td className="cell">
                  {member.status !== 'active' ? (
                    <span className="team__access team__access--off">{STATUS_LABEL[member.status]}</span>
                  ) : member.canSignIn ? (
                    <span className="team__access team__access--on">Peut se connecter</span>
                  ) : (
                    <span className="team__access team__access--none">Aucun accès</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !query.isLoading && (
              <tr>
                <td className="cell" colSpan={6}>
                  <span className="section__empty">Personne ne correspond à ce filtre.</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {selected && (
          <aside className="team__detail" aria-label={`Détails — ${selected.name}`}>
            <header className="team__detailHead">
              <span
                className="team__swatch team__swatch--lg"
                style={{ background: selected.color ?? 'var(--zinc-400)' }}
                aria-hidden="true"
              />
              <div>
                <h2 className="team__detailName">{selected.name}</h2>
                <span className="team__detailMeta">
                  {selected.email ?? 'Aucun courriel'}
                  {selected.phone && ` · ${formatPhone(selected.phone)}`}
                </span>
              </div>
              <button
                type="button"
                className="team__close"
                onClick={() => setSearch({ person: undefined })}
                aria-label="Fermer"
              >
                ×
              </button>
            </header>

            {/*
              * The consequence of the role, spelled out. This is the whole reason the
              * capability model exists rather than an administrator flag: it can be read.
              */}
            <section className="team__section">
              <h3 className="label">Rôle — {label(selected.role.name)}</h3>
              {/* Straight from the role row, so an edited role reads correctly here without
                  anything in this file needing to know about it. */}
              <p className="team__purpose">{label(roleOf(selected)?.description ?? null)}</p>
              <ul className="team__caps">
                {selected.role.capabilities
                  .filter((c) => CAPABILITY_LABEL[c])
                  .map((c) => (
                    <li key={c}>{CAPABILITY_LABEL[c]}</li>
                  ))}
              </ul>
              {selected.role.key === 'technician' && (
                <p className="team__cant">
                  Ne voit pas : les revenus de l’entreprise, les prix de revient, les taux horaires
                  de ses collègues, ni les travaux qui ne lui sont pas assignés.
                </p>
              )}
              {selected.role.key === 'lead_technician' && (
                <p className="team__cant">
                  Ne voit pas : les prix de revient, les marges, les profits, ni les taux horaires.
                  Ne crée pas de travaux, de soumissions ni de factures.
                </p>
              )}
            </section>

            {/*
              * The cross-business warning. The single most useful thing on this panel, and
              * the thing the previous per-business records made impossible to state.
              */}
            {selected.otherTenants.length > 0 && (
              <section className="team__section team__section--warn">
                <h3 className="label">Aussi dans d’autres entreprises</h3>
                <ul className="team__tenants">
                  {selected.otherTenants.map((t) => (
                    <li key={t.tenantId}>
                      <span>{t.tenantName}</span>
                      <span className="team__tenantRole">{label(t.roleName)}</span>
                      <span className={`team__access team__access--${t.status === 'active' ? 'on' : 'off'}`}>
                        {t.status === 'active' ? 'Actif' : 'Désactivé'}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="team__note">
                  Retirer l’accès ici ne change rien dans ces entreprises. Le nom, la couleur et
                  le téléphone sont partagés : les modifier les modifie partout.
                </p>
              </section>
            )}

            <section className="team__section">
              <h3 className="label">Activité</h3>
              <dl className="team__stats">
                <div>
                  <dt>Visites</dt>
                  <dd className="tnum">{selected.activity.visits}</dd>
                </div>
                <div>
                  <dt>Heures inscrites</dt>
                  <dd className="tnum">{Math.round(selected.activity.loggedMinutes / 60)} h</dd>
                </div>
                <div>
                  {/* Bookings run well into the future here — one of these is scheduled a
                      year out — so calling a date in the future "last" would be wrong. */}
                  <dt>
                    {selected.activity.lastVisitAt && new Date(selected.activity.lastVisitAt) > new Date()
                      ? 'Prochaine visite'
                      : 'Dernière visite'}
                  </dt>
                  <dd>
                    {selected.activity.lastVisitAt
                      ? new Date(selected.activity.lastVisitAt).toLocaleDateString('fr-CA')
                      : '—'}
                  </dd>
                </div>
                {data?.ratesVisible && (
                  <div>
                    <dt>Taux horaire</dt>
                    <dd>{selected.hourlyRate ? `${selected.hourlyRate} $/h` : <span className="team__unset">non défini</span>}</dd>
                  </div>
                )}
              </dl>
            </section>

            {/*
              * Editing is deliberately not wired up yet: a role change revokes sessions and
              * must run against the real API with the last-administrator check. A form that
              * looked like it worked and silently did nothing would be worse than none.
              */}
            <footer className="team__pending">
              La modification des rôles, des taux et des accès passe par l’API réelle — pas
              encore branchée sur cet aperçu.
            </footer>
          </aside>
        )}
      </div>
      )}
    </div>
  );
}

/**
 * The roles themselves.
 *
 * The member count on each row is the point. A permission model you cannot audit is the one
 * the previous system had — 35 keys spread across 12 people, and no way to ask "who can see
 * cost prices?" without reading every record. Here that question is one row.
 */
function RolesView({ data, members }: { data: RolesData; members: Member[] }) {
  return (
    <div className="roles">
      <p className="roles__intro">
        Un rôle est un ensemble de permissions portant un nom. Les accès se donnent par rôle,
        jamais personne par personne — c’est ce qui permet de répondre à « qui peut voir les
        prix de revient ? » d’un coup d’œil.
      </p>

      <div className="roles__grid">
        {data.roles.map((role) => {
          const holders = members.filter((m) => m.status === 'active' && m.role.key === role.key);
          return (
            <article key={role.id} className="roleCard">
              <header className="roleCard__head">
                <h3 className="roleCard__name">{label(role.name)}</h3>
                <span className={`roleCard__landing roleCard__landing--${role.landing}`}>
                  {role.landing === 'field' ? 'Application mobile' : 'Portail'}
                </span>
                {role.isProtected && (
                  <span className="roleCard__locked" title="Ce rôle ne peut pas être modifié">
                    Verrouillé
                  </span>
                )}
              </header>

              {role.description && <p className="roleCard__desc">{label(role.description)}</p>}

              <p className="roleCard__count">
                {holders.length === 0 ? (
                  <span className="team__unset">Personne</span>
                ) : (
                  holders.map((m) => m.name).join(', ')
                )}
              </p>

              <details className="roleCard__caps">
                <summary>{role.capabilities.length} permissions</summary>
                <ul>
                  {role.capabilities
                    .filter((c) => CAPABILITY_LABEL[c])
                    .map((c) => (
                      <li key={c}>{CAPABILITY_LABEL[c]}</li>
                    ))}
                </ul>
              </details>
            </article>
          );
        })}
      </div>

      {/*
        * Editing is not wired up yet: saving a role changes what everybody can do on their
        * very next request, and it needs the real API's guards — you cannot grant a permission
        * you lack, and you cannot remove the last person able to undo it.
        */}
      <p className="team__pending">
        La création et la modification des rôles passent par l’API réelle — pas encore branchées
        sur cet aperçu. Les garde-fous existent déjà côté serveur : impossible d’accorder une
        permission qu’on n’a pas soi-même, ni de retirer le dernier accès de gestion.
      </p>
    </div>
  );
}
